import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
  MenuItem,
  InputAdornment,
  IconButton,
  FormHelperText,
  Chip,
  Dialog,
  Grid,
  useTheme,
  Paper,
  Button,
} from "@mui/material";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import Divider from "@mui/material/Divider";
import useMediaQuery from "@mui/material/useMediaQuery";
import "./App.css";
import { Chrono } from "react-chrono";
import Loader from "./Loader";
import axios from "./axiosConfig";
import AdjustIcon from "@mui/icons-material/Adjust";
import CheckIcon from '@mui/icons-material/Check';
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";

import images from "./logo2.png";
import images3 from "./grocery-cart.png";
import images2 from "./ticket.png";
import images1 from "./transfer2.png";
import ShipmentTrackingMobile from "./ShipmentTrackingMobile";
import { TimelineOppositeContent } from "@mui/lab";

const ShipmentTracking = () => {
  const { db, trackno } = useParams();
  const tokent =
    "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJldmVudGxpc3QiOlt7IlVzZXJJRCI6IjEiLCJMb2dpbkNvZGUiOiIwMSIsIkxvZ2luTmFtZSI6IkFkbWluIiwiRW1haWxJZCI6ImFkbWluQGdtYWlsLmNvbSIsIlVzZXJUeXBlIjoiQURNSU4ifV0sImlhdCI6MTYzODM1NDczMX0.ZW6zEHIXTxfT-QWEzS6-GuY7bRupf2Jc_tp4fXIRabQ";
  const [country, setCountry] = useState(db ? db.toUpperCase() : "IN");
  const [trackingNumber, setTrackingNumber] = useState(trackno || "");
  const [errorMessage, setErrorMessage] = useState("");
  const [openLoader, setOpenLoader] = useState(false);
  const [countryData, setCountryData] = useState([]);
  const [headerData, setHeaderData] = useState(null);
  const [bookingData, setBookingData] = useState([]);

  const [trackingMaster, setTrackingMaster] = useState([]);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [firstTrackClicked, setFirstTrackClicked] = useState(false);
  
  // New states for animation
  const [activeStep, setActiveStep] = useState(-1);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isCardReady, setIsCardReady] = useState(false); // Track if card is ready for animation
  const timelineRef = useRef(null);
  const cardRef = useRef(null);
  
  // Ref to track if animation has started
  const animationStartedRef = useRef(false);

  const theme = useTheme();
  const belowMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    GetDBCountries();
  }, []);

  useEffect(() => {
    if (db && trackno) {
      handleTrack();
    }
  }, [db, trackno]);

  // Effect to start animation when card is ready
  useEffect(() => {
    if (isCardReady && !animationStartedRef.current && completedSteps.length > 0 && trackingMaster.length > 0) {
      // Use requestAnimationFrame to ensure the DOM is updated
      requestAnimationFrame(() => {
        // Small delay to ensure card is visible and rendered
        setTimeout(() => {
          // Scroll to the card first
          if (cardRef.current) {
            cardRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
            
            // Wait for scroll to complete, then start animation
            setTimeout(() => {
              animateTimeline();
              animationStartedRef.current = true;
            }, 500);
          } else {
            // Fallback if cardRef is not available
            animateTimeline();
            animationStartedRef.current = true;
          }
        }, 300);
      });
    }
  }, [isCardReady, completedSteps.length, trackingMaster.length]);

  // get all Active Buyer data
  const GetDBCountries = async () => {
    try {
      const res = await axios.instance.get("/IN/GetDBCountries", {
        headers: { Authorization: tokent, "Content-Type": "application/json" },
      });
      setCountryData(res.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchTrackerMaster = async () => {
    try {
      const url = `/${country}/GetTrackingMaster`;
      const response = await axios.instance.post(
        url,
        { TrackNo: trackingNumber },
        {
          headers: { 
            Authorization: tokent, 
            "Content-Type": "application/json" 
          },
        }
      );
      
      const masterData = response.data;
      setTrackingMaster(masterData);
      
      return masterData;
    } catch (error) {
      console.error("Error fetching tracker master:", error);
      throw error;
    }
  };

  const fetchTrackingDetails = async () => {
    try {
      const url = `/${country}/GetTrackingDetails`;
      const response = await axios.instance.post(
        url,
        { TrackNo: trackingNumber },
        {
          headers: {
            Authorization: tokent,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data.resultSet1[0].valid === "Invalid Tracking Number") {
        setErrorMessage("Invalid Tracking Number");
        return null;
      }
      
      const { resultSet1, resultSet2 } = response.data;
      setHeaderData(resultSet1[0]);
      setBookingData(resultSet2);
      
      return resultSet2;
    } catch (error) {
      console.error("Error fetching tracking details:", error);
      throw error;
    }
  };

  // Function to determine completed steps
  const findCompletedSteps = (actualTrackingData, master = trackingMaster) => {
    if (!master.length || !actualTrackingData || !actualTrackingData.length) return [];
    
    // Extract completed track names from actual data
    const completedTrackNames = actualTrackingData.map(item => item.TrackName);
    
    // Sort completed steps according to master order
    const sortedCompleted = master.filter(masterStep => 
      completedTrackNames.some(name => name.toLowerCase() === masterStep.TrackName.toLowerCase())
    );
    
    setCompletedSteps(sortedCompleted);
    return sortedCompleted;
  };

  // Animate the timeline step by step
  const animateTimeline = () => {
    // Prevent multiple animations
    if (isAnimating || animationStartedRef.current) return;
    
    setIsAnimating(true);
    setActiveStep(-1);
    
    const steps = completedSteps;
    if (steps.length === 0) {
      setIsAnimating(false);
      return;
    }
    
    // Find the index of "PARCEL BOOKED" in master list
    const parcelBookedIndex = trackingMaster.findIndex(
      step => step.TrackName === "PARCEL BOOKED"
    );
    
    if (parcelBookedIndex !== -1) {
      // Start animation from PARCEL BOOKED
      setTimeout(() => {
        setActiveStep(parcelBookedIndex);
      }, 300);
      
      // Animate subsequent completed steps
      let currentIndex = parcelBookedIndex;
      const animateNextStep = () => {
        currentIndex++;
        
        // Find the next completed step in master order
        while (currentIndex < trackingMaster.length) {
          const step = trackingMaster[currentIndex];
          if (completedSteps.some(completed => (completed.TrackName).toLowerCase() === (step.TrackName).toLowerCase())) {
            setTimeout(() => {
              setActiveStep(currentIndex);
            }, 100);
            
            // Continue animating if there are more steps
            if (currentIndex < trackingMaster.length - 1) {
              setTimeout(animateNextStep, 1200); // Delay between steps
            } else {
              setTimeout(() => setIsAnimating(false), 1000);
            }
            return;
          }
          currentIndex++;
        }
        
        // If no more completed steps found
        setTimeout(() => setIsAnimating(false), 1000);
      };
      
      // Start animating subsequent steps after a delay
      setTimeout(animateNextStep, 1000);
    } else {
      // Fallback if PARCEL BOOKED not found
      setTimeout(() => {
        setActiveStep(0);
        setIsAnimating(false);
      }, 1000);
    }
  };

  // Helper function to check if a step is currently active (animating to)
  const isStepActive = (index) => {
    return isAnimating && activeStep >= index;
  };

  // Helper function to check if a step is completed (already animated)
  const isStepCompleted = (trackName, index) => {
    if (!isAnimating) {
      // When animation is done, show all completed steps
      return completedSteps.some(step => (step.TrackName).toLowerCase() === (trackName).toLowerCase());
    }
    
    // During animation, show as completed if it's before or equal to active step
    if (activeStep >= index) {
      return completedSteps.some(step => (step.TrackName).toLowerCase() === (trackName).toLowerCase());
    }
    
    return false;
  };

  const handleTrack = async () => {
    if (!trackingNumber) {
      setErrorMessage("Tracking number cannot be empty.");
      return;
    }
    setErrorMessage("");
    setOpenLoader(true);
    setActiveStep(-1);
    setIsAnimating(false);
    setFirstTrackClicked(false);
    setIsCardReady(false);
    animationStartedRef.current = false; // Reset animation flag

    try {
      const masterData = await fetchTrackerMaster();
      const actualData = await fetchTrackingDetails();
      if (masterData && actualData) {
        // Compute completed steps
        const completed = findCompletedSteps(actualData, masterData);
        
        // Mark card as ready - this will trigger the animation effect
        setIsCardReady(true);
      }
    } catch (err) {
      console.error("Error during tracking fetch:", err);
    } finally {
      setOpenLoader(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleTrack();
    }
  };

  // Calculate the fixed number of packages based on the "PARCEL BOOKED" status
  const fixedPackageCount = bookingData
    .filter((item) => item.TrackName === "PARCEL BOOKED")
    .reduce((uniquePackages, item) => {
      if (!uniquePackages.includes(item.BoxNo)) {
        uniquePackages.push(item.BoxNo);
      }
      return uniquePackages;
    }, []).length;

  // Animation styles
  const animationStyles = `
    @keyframes drawLine {
      from {
        height: 0;
      }
      to {
        height: 100%;
      }
    }
    
    @keyframes pulseDot {
      0% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
      }
      70% {
        transform: scale(1.1);
        box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
      }
      100% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
      }
    }
    
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @keyframes glow {
      0%, 100% {
        box-shadow: 0 0 5px rgba(76, 175, 80, 0.5);
      }
      50% {
        box-shadow: 0 0 20px rgba(76, 175, 80, 0.8);
      }
    }
    
    /* Add a subtle shake animation for attention */
    @keyframes subtleShake {
      0%, 100% {
        transform: translateX(0);
      }
      10%, 30%, 50%, 70%, 90% {
        transform: translateX(-2px);
      }
      20%, 40%, 60%, 80% {
        transform: translateX(2px);
      }
    }
    
    .animated-connector {
      position: relative;
      overflow: hidden;
    }
    
    .animated-connector.active::before {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 3px;
      height: 0;
      background: linear-gradient(to bottom, #4caf50, #2e7d32);
      animation: drawLine 0.8s ease-out forwards;
      z-index: 1;
    }
    
    .pulse-dot {
      animation: pulseDot 2s infinite;
    }
    
    .fade-in-up {
      animation: fadeInUp 0.6s ease-out forwards;
    }
    
    .glow-card {
      animation: glow 2s infinite;
    }
    
    .attention-shake {
      animation: subtleShake 0.5s ease-in-out;
    }
  `;

  return (
    <div
      style={{
        minWidth: "100vw",
      }}
    >
      <style>{animationStyles}</style>
      
      {belowMediumScreen ? (
        <ShipmentTrackingMobile />
      ) : (
        <>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1100,
              backgroundColor: "#0072E5",
              padding: "6px 16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              color: "#fff",
              boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <PhoneIcon />
                <Typography variant="subtitle2" sx={{ color: "#fff" }}>
                  <a
                    href="tel:+442037907773"
                    style={{ color: "#fff", textDecoration: "none" }}
                  >
                    020 3790 7773
                  </a>
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <EmailIcon />
                <Typography variant="subtitle2" sx={{ color: "#fff" }}>
                  <a
                    href="mailto:info@teeparam.com"
                    style={{ color: "#fff", textDecoration: "none" }}
                  >
                    info@teeparam.com
                  </a>
                </Typography>
              </Box>
            </Box>

            <Button
              sx={{
                backgroundColor: "#00A44B",
                color: "#fff",
                padding: "4px 16px",
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: "#007e3a",
                },
              }}
              size="small"
              onClick={() =>
                window.open("https://teeparamexchange.co.uk/", "_blank")
              }
            >
              MONEY TRANSFER
            </Button>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={headerData ? 6 : 12}>
              <Box
                sx={{
                  padding: 4,
                  backgroundColor: "white",
                  maxHeight: "85vh",
                  minHeight: headerData ? "75vh" : "70vh",
                  borderRadius: 10,
                }}
                m={4}
              >
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    textAlign: "center",
                    padding: "16px",
                  }}
                >
                  <a
                    href="https://www.teeparam.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <div>
                      <img src={images} 
                       style={{
                  objectFit: 'contain',
                  maxWidth: '15%',
                }}
                      alt="logo" />
                    </div>
                  </a>
                  <Typography
                    variant={belowMediumScreen ? "subtitle2" : "h5"}
                    sx={{
                      marginTop: 2,
                      marginBottom: 2,
                      fontWeight: "bold",
                      color: "#536493",
                    }}
                  >
                    Track Your Shipment Status
                  </Typography>
                  {belowMediumScreen ? (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          marginTop: 2,
                          borderRadius: 30,
                        }}
                      >
                        <TextField
                          fullWidth
                          variant="outlined"
                          placeholder="Enter your tracking number"
                          value={trackingNumber}
                          onChange={(e) => setTrackingNumber(e.target.value)}
                          onKeyPress={handleKeyPress}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <TextField
                                  select
                                  value={country}
                                  onChange={(e) => setCountry(e.target.value)}
                                  variant="outlined"
                                  sx={{
                                    backgroundColor: "#fff",
                                    border: "none",
                                    "& .MuiSelect-select": {
                                      padding: "12px",
                                      fontWeight: "bold",
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      border: "none",
                                    },
                                  }}
                                  SelectProps={{
                                    renderValue: (selected) => {
                                      const selectedCountry = countryData.find(
                                        (option) =>
                                          option.CountryCode === selected
                                      );
                                      return selectedCountry
                                        ? selectedCountry.CountryCode
                                        : selected;
                                    },
                                  }}
                                >
                                  {countryData.map((option) => (
                                    <MenuItem
                                      key={option.CountryCode}
                                      value={option.CountryCode}
                                    >
                                      {option.CountryName}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            width: belowMediumScreen ? "100vw" : "50vw",
                            backgroundColor: "#fff",
                            border: "none",
                          }}
                          error={Boolean(errorMessage)}
                        />
                      </Box>
                      <Button
                        variant="outlined"
                        color="info"
                        onClick={handleTrack}
                        size="small"
                        endIcon={<ArrowRightIcon />}
                        sx={{ borderRadius: 10, mt: 2 }}
                      >
                        Track
                      </Button>
                    </>
                  ) : (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          marginTop: 2,
                          borderRadius: 30,
                        }}
                      >
                        <TextField
                          fullWidth
                          variant="outlined"
                          placeholder="Enter your tracking number"
                          value={trackingNumber}
                          onChange={(e) => setTrackingNumber(e.target.value)}
                          onKeyPress={handleKeyPress}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <TextField
                                  select
                                  value={country}
                                  onChange={(e) => setCountry(e.target.value)}
                                  variant="outlined"
                                  sx={{
                                    backgroundColor: "#fff",
                                    border: "none",
                                    "& .MuiSelect-select": {
                                      padding: "12px",
                                      fontWeight: "bold",
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      border: "none",
                                    },
                                  }}
                                >
                                  {countryData.map((option) => (
                                    <MenuItem
                                      key={option.CountryCode}
                                      value={option.CountryCode}
                                    >
                                      {option.CountryName}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <Button
                                  variant="outlined"
                                  color="info"
                                  onClick={handleTrack}
                                  size="small"
                                  endIcon={<ArrowRightIcon />}
                                  sx={{ borderRadius: 10 }}
                                >
                                  Track
                                </Button>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            width: belowMediumScreen ? "100vw" : "50vw",
                            backgroundColor: "#fff",
                            border: "none",
                          }}
                          error={Boolean(errorMessage)}
                        />
                      </Box>
                    </>
                  )}
                  {errorMessage && (
                    <FormHelperText
                      error
                      sx={{ textAlign: "center", marginTop: 1 }}
                    >
                      {errorMessage}
                    </FormHelperText>
                  )}
                </Box>

                <Divider fullWidth sx={{ mt: 1, mx: 10, mb: 3 }}>
                  <Chip
                    label="Our Services"
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                </Divider>
                <Grid container spacing={2}>
                  {[
                    {
                      name: headerData
                        ? "Money <br /> Transfer"
                        : "Money Transfer",
                      imgSrc: images1,
                      link: "https://teeparamexchange.co.uk/",
                    },
                    {
                      name: headerData
                        ? "Air <br /> Ticketing"
                        : "Air Ticketing",
                      imgSrc: images2,
                      link: "https://www.whitelabelhosting.co.uk/",
                    },
                    // {
                    //   name: headerData
                    //     ? "FMCG <br /> Wholesaler"
                    //     : "FMCG Wholesaler",
                    //   imgSrc: images3,
                    //   link: "https://www.teeparamwholesale.co.uk/",
                    // },
                  ].map((service) => (
                    <Grid item xs={12} sm={12} md={6} key={service.name}>
                      <a
                        href={service.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: "none" }}
                      >
                        <Card
                          sx={{
                            boxShadow: 2,
                            textAlign: "center",
                            borderRadius: 7,
                            padding: 1,
                            mx: 2,
                            boxShadow:
                              "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
                            transition:
                              "transform 0.3s ease, box-shadow 0.3s ease",
                            "&:hover": {
                              transform: "translateY(-10px)",
                              boxShadow: 4,
                            },
                          }}
                        >
                          <CardContent>
                            <Box
                              sx={{
                                width: belowMediumScreen ? 20 : 20,
                                height: belowMediumScreen ? 20 : 20,
                                backgroundColor: "#2F3645",
                                borderRadius: "50%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                margin: "0 auto",
                                marginBottom: 1,
                                padding: 2,
                              }}
                            >
                              <img
                                src={service.imgSrc}
                                alt={service.name}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                }}
                              />
                            </Box>
                            <Typography
                              variant={
                                belowMediumScreen ? "subtitle2" : "subtitle2"
                              }
                              color="#2F3645"
                              sx={{ fontWeight: "bold" }}
                              dangerouslySetInnerHTML={{ __html: service.name }}
                            />
                          </CardContent>
                        </Card>
                      </a>
                    </Grid>
                  ))}
                </Grid>

                <Dialog
                  open={openLoader}
                  aria-labelledby="alert-dialog-title"
                  aria-describedby="alert-dialog-description"
                  fullWidth
                  PaperProps={{
                    sx: {
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    },
                  }}
                >
                  <Stack
                    sx={{
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                      backgroundColor: "#ffffff00",
                    }}
                  >
                    <Loader />
                  </Stack>
                </Dialog>
              </Box>
            </Grid>
            {headerData && (
              <Grid item xs={6} ref={cardRef}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-around",
                    animation: isCardReady ? "fadeInUp 0.5s ease-out" : "none",
                    opacity: isCardReady ? 1 : 0,
                    transform: isCardReady ? "translateY(0)" : "translateY(20px)",
                    transition: "opacity 0.5s ease-out, transform 0.5s ease-out",
                  }}
                  m={4}
                >
                  <Card
                    sx={{
                      boxShadow: 0,
                      borderRadius: 10,
                      maxHeight: "94vh",
                      overflowY: "scroll",
                      // Show a subtle loading state before animation
                      ...(!isCardReady && {
                        bgcolor: "rgba(245, 245, 245, 0.8)",
                      }),
                    }}
                    elevation={0}
                    pb={2}
                    fullWidth
                  >
                    <CardContent>
                      {headerData && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-around",
                            position: "sticky",
                            top: 0,
                            backgroundColor: "#fff",
                            zIndex: 1000,
                          }}
                        >
                          <Card
                            sx={{
                              boxShadow: 0,
                              width: belowMediumScreen ? "100vw" : "70vw",
                            }}
                            elevation={0}
                            pt={2}
                            fullWidth
                          >
                            <CardContent>
                              <Divider fullWidth sx={{ mt: 1 }}>
                                <Chip
                                  label="Tracking Status"
                                  size="small"
                                  color="primary"
                                />
                              </Divider>

                              <Stack
                                direction={belowMediumScreen ? "column" : "row"}
                                divider={
                                  <Divider
                                    orientation={
                                      belowMediumScreen
                                        ? "horizontal"
                                        : "vertical"
                                    }
                                    flexItem
                                  />
                                }
                                mt={3}
                                mb={0}
                                justifyContent={"space-evenly"}
                                alignItems={"center"}
                                component={Paper}
                                elevation={2}
                                p={2}
                                borderRadius={7}
                              >
                                <Stack
                                  direction="column"
                                  justifyContent={"center"}
                                  alignItems={"center"}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    color="#50B498"
                                    align="center"
                                  >
                                    Origin
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    align="center"
                                    textTransform={"capitalize"}
                                  >
                                    {`${headerData.SCity}, ${headerData.SCountry}` ||
                                      "N/A"}
                                  </Typography>
                                </Stack>

                                <Stack
                                  direction="column"
                                  justifyContent={"center"}
                                  alignItems={"center"}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    color="#50B498"
                                    align="center"
                                  >
                                    Destination
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    align="center"
                                    textTransform={"capitalize"}
                                  >
                                    {`${headerData.RCity}, ${headerData.RCountry}` ||
                                      "N/A"}
                                  </Typography>
                                </Stack>

                                <Stack
                                  direction="column"
                                  justifyContent={"center"}
                                  alignItems={"center"}
                                >
                                  {/* <Typography
                                    variant="subtitle2"
                                    color="#50B498"
                                    align="center"
                                  >
                                    Bill Weight
                                  </Typography>
                                  <Typography variant="body2" align="center">
                                    {headerData.TotalWeight || "0"} kg
                                  </Typography> */}
                                   <Typography
                                    variant="subtitle2"
                                    color="#50B498"
                                    align="center"
                                  >
                                    Actual Weight
                                  </Typography>
                                  <Typography variant="body2" align="center">
                                    {headerData.ACWeight || "0"} kg
                                     </Typography>
                                </Stack>
                                {/* <Stack
                                  direction="column"
                                  justifyContent={"center"}
                                  alignItems={"center"}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    color="#50B498"
                                    align="center"
                                  >
                                    Dimensions
                                  </Typography>
                                  <Typography variant="body2" align="center">
                                    {/* {headerData.ACWeight || "0"} kg */}
                                    {/* {headerData.BoxDimensions
                                        ?.split(",")
                                        .map((box, index) => (
                                          <div key={index}>{box.trim()}</div>
                                        ))}
                                  </Typography>
                                </Stack> */}
                                <Stack
                            direction="column"
                            justifyContent={"center"}
                            alignItems={"center"}
                            sx={{
                              // Apply styles only to the dimensions stack
                              maxHeight: belowMediumScreen ? "70px" : "80px",
                              overflowY: 'auto',
                              overflowX: 'hidden',
                              width: '50%',
                              paddingRight: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              color="#50B498"
                              align="center"
                              sx={{ mb: 0.5 }}
                            >
                              Dimensions / Box Type
                            </Typography>
                            <Box
                              sx={{
                                width: '100%',
                                maxHeight: belowMediumScreen ? "80px" : "100px",
                                overflowY: 'auto',
                                '&::-webkit-scrollbar': {
                                  width: '4px',
                                },
                                '&::-webkit-scrollbar-track': {
                                  background: '#f1f1f1',
                                  borderRadius: '2px',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                  background: '#888',
                                  borderRadius: '2px',
                                },
                                '&::-webkit-scrollbar-thumb:hover': {
                                  background: '#555',
                                },
                              }}
                            >
                              {headerData.BoxDimensions ? (
                                headerData.BoxDimensions
                                  .split(",")
                                  .filter(box => box.trim()) // Remove empty strings
                                  .map((box, index) => (
                                    <Typography 
                                      key={index} 
                                      variant="body2" 
                                      align="center"
                                      sx={{ 
                                        mb: 0.5,
                                        fontSize: belowMediumScreen ? '0.75rem' : '0.875rem',
                                      }}
                                    >
                                      {box.trim()}
                                    </Typography>
                                  ))
                              ) : (
                                <Typography variant="body2" align="center">
                                  N/A
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Box>
                      )}

                      <Stack
                        justifyContent="center"
                        alignItems="center"
                        sx={{ 
                          width: "100%",
                          // Control visibility based on animation state
                          opacity: isCardReady ? 1 : 0,
                          transition: "opacity 0.3s ease",
                        }}
                      >
                        <Timeline
                          position="alternate"
                          ref={timelineRef}
                          sx={{
                            maxWidth: belowMediumScreen ? "100vw" : "800px",
                            width: "100%",
                            '& .MuiTimelineConnector-root': {
                              transition: 'background-color 0.5s ease',
                            },
                            // Initially hide timeline dots during loading
                            ...(!isCardReady && {
                              '& .MuiTimelineDot-root': {
                                visibility: 'hidden',
                              },
                              '& .MuiTimelineConnector-root': {
                                visibility: 'hidden',
                              },
                            }),
                          }}
                        >
                          {trackingMaster.length > 0 ? (
                            trackingMaster.map((masterStep, index) => {
                              const isCompleted = isStepCompleted(masterStep.TrackName, index);
                              const isActive = isStepActive(index);
                              const actualData = bookingData.filter(
                                item => (item.TrackName).toLowerCase() === (masterStep.TrackName).toLowerCase()
                              );
                              
                              return (
                                <TimelineItem key={index}>
                                  <TimelineOppositeContent color="text.secondary">
                                    <Typography
                                      variant="subtitle2"
                                      color={isCompleted ? "success.main" : "text.secondary"}
                                      textTransform={"capitalize"}
                                      fontWeight="bold"
                                      sx={{
                                        opacity: isActive ? 1 : (isCompleted ? 1 : 0.6),
                                        transform: isActive ? 'translateX(5px)' : 'translateX(0)',
                                        transition: 'all 0.5s ease',
                                        transitionDelay: `${index * 0.1}s`,
                                      }}
                                      className={isActive ? "fade-in-up" : ""}
                                    >
                                      {masterStep.TrackName}
                                    </Typography>
                                    <Typography
                                      variant="subtitle2"
                                      color="#16325B"
                                      fontWeight={"bold"}
                                    >
                                      No. of Packages: {headerData.NoofBox || "N/A"}
                                    </Typography>
                                  </TimelineOppositeContent>
                                  <TimelineSeparator>
                                    <TimelineDot
                                      variant={isCompleted ? "filled" : "outlined"}
                                      sx={{
                                        bgcolor: isActive ? 'success.main' : 
                                                 (isCompleted ? 'success.light' : 'transparent'),
                                        color: isCompleted ? '#fff' : 'grey.500',
                                        borderColor: isCompleted ? 'success.main' : 'grey.400',
                                        borderWidth: isCompleted ? 2 : 1,
                                        transform: isActive ? 'scale(1.2)' : 'scale(1)',
                                        transition: 'all 0.5s ease',
                                        transitionDelay: `${index * 0.1}s`,
                                        position: 'relative',
                                        zIndex: 2,
                                        ...(isActive && {
                                          animation: 'pulseDot 2s infinite',
                                        })
                                      }}
                                    >
                                      {isCompleted ? (
                                        <CheckIcon sx={{
                                          fontSize: 16,
                                          opacity: isActive ? 0.9 : 1,
                                          transition: 'opacity 0.3s ease',
                                        }} />
                                      ) : (
                                        <AdjustIcon sx={{
                                          fontSize: 16,
                                          opacity: isActive ? 0.8 : 0.6,
                                        }} />
                                      )}
                                    </TimelineDot>
                                    {index < trackingMaster.length - 1 && (
                                      <TimelineConnector 
                                        sx={{ 
                                          bgcolor: isCompleted ? "success.light" : "grey.200",
                                          position: 'relative',
                                          overflow: 'visible',
                                          ...(isActive && {
                                            '&::before': {
                                              content: '""',
                                              position: 'absolute',
                                              top: 0,
                                              left: '50%',
                                              transform: 'translateX(-50%)',
                                              width: '3px',
                                              height: 0,
                                              background: 'linear-gradient(to bottom, #4caf50, #2e7d32)',
                                              animation: 'drawLine 0.8s ease-out forwards',
                                              zIndex: 1,
                                            }
                                          })
                                        }} 
                                      />
                                    )}
                                  </TimelineSeparator>
                                  <TimelineContent>
                                    <Card
                                      sx={{
                                        boxShadow: 0,
                                        padding: 2,
                                        elevation: 0,
                                        mb: 4,
                                        border: isActive ? "2px solid" : 
                                               (isCompleted ? "1px solid" : "none"),
                                        borderColor: isActive ? "success.main" : 
                                                    (isCompleted ? "success.light" : "transparent"),
                                        bgcolor: isActive ? "rgba(76, 175, 80, 0.05)" : 
                                                (isCompleted ? "rgba(76, 175, 80, 0.03)" : "transparent"),
                                        transform: isActive ? 'translateX(-5px)' : 'translateX(0)',
                                        transition: 'all 0.5s ease',
                                        transitionDelay: `${index * 0.1}s`,
                                        opacity: isCompleted ? 1 : 0.7,
                                      }}
                                      className={isActive ? "glow-card" : ""}
                                    >
                                      {actualData.length > 0 ? (
                                        actualData.map((packageData, pkgIdx) => (
                                          <div key={pkgIdx} style={{ marginBottom: 8 }}>
                                            <Typography
                                              variant="caption"
                                              color={isActive ? "success.dark" : 
                                                     (isCompleted ? "success.main" : "text.secondary")}
                                              sx={{
                                                transition: 'color 0.3s ease',
                                                display: 'block',
                                                marginBottom: 0.5,
                                              }}
                                            >
                                              <span
                                                style={{
                                                  color: isActive ? "#2e7d32" : 
                                                         (isCompleted ? "#508D4E" : "#9e9e9e"),
                                                  fontWeight: "bold",
                                                  transition: 'color 0.3s ease',
                                                }}
                                              >
                                                Pkg {packageData.BoxNo}
                                              </span>
                                              : {packageData.BookTrackDate}{" "}
                                              {packageData.BookTrackTime}
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              textTransform={"lowercase"}
                                              color={isCompleted ? "success.dark" : "text.primary"}
                                              sx={{
                                                opacity: isActive ? 0.9 : 
                                                       (isCompleted ? 0.8 : 0.6),
                                                transition: 'opacity 0.3s ease',
                                                fontStyle: isCompleted ? 'normal' : 'italic',
                                               
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                                overflowWrap: "anywhere",

                                              }}
                                            >
                                              {packageData.Remarks || " "}
                                            </Typography>
                                          </div>
                                        ))
                                      ) : (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          fontStyle="italic"
                                          sx={{
                                            opacity: isCompleted ? 0.8 : 0.4,
                                            transition: 'opacity 0.3s ease',
                                          }}
                                        >
                                          {isCompleted ? "Completed" : "Pending"}
                                        </Typography>
                                      )}
                                    </Card>
                                  </TimelineContent>
                                </TimelineItem>
                              );
                            })
                          ) : (
                            bookingData
                              .reduce((acc, curr) => {
                                const existingTrack = acc.find(
                                  (item) => (item.TrackName).toLowerCase() === (curr.TrackName).toLowerCase()
                                );
                                if (!existingTrack) {
                                  acc.push({
                                    TrackName: curr.TrackName,
                                    Remarks: curr.Remarks,
                                    packages: [],
                                  });
                                }

                                const currentIndex = acc.findIndex(
                                  (item) => (item.TrackName).toLowerCase() === (curr.TrackName).toLowerCase()
                                );

                                acc[currentIndex].packages.push({
                                  BoxNo: curr.BoxNo,
                                  BookTrackDate: curr.BookTrackDate,
                                  BookTrackTime: curr.BookTrackTime,
                                  Remarks: curr.Remarks,
                                });
                                return acc;
                              }, [])
                              .reverse()
                              .map((event, index) => (
                                <TimelineItem key={index}>
                                  <TimelineOppositeContent color="text.secondary">
                                    <Typography
                                      variant="subtitle2"
                                      color="primary"
                                      textTransform={"capitalize"}
                                      fontWeight="bold"
                                    >
                                      {event.TrackName}
                                    </Typography>
                                    <Typography
                                      variant="subtitle2"
                                      color="#16325B"
                                      fontWeight={"bold"}
                                    >
                                      No. of Packages: {headerData.NoofBox}
                                    </Typography>
                                  </TimelineOppositeContent>
                                  <TimelineSeparator>
                                    <TimelineDot color="primary">
                                      <AdjustIcon />
                                    </TimelineDot>
                                    {index < bookingData.length - 2 && (
                                      <TimelineConnector />
                                    )}
                                  </TimelineSeparator>
                                  <TimelineContent>
                                    <Card
                                      sx={{
                                        boxShadow: 0,
                                        padding: 1,
                                        elevation: 0,
                                        mb: 4,
                                      }}
                                    >
                                      {event.packages
                                        .reverse()
                                        .map((packageData, pkgIdx) => (
                                          <div key={pkgIdx} style={{ marginBottom: 5 }}>
                                            <Typography
                                              variant="caption"
                                              color="textSecondary"
                                            >
                                              <span
                                                style={{
                                                  color: "#508D4E",
                                                  fontWeight: "bold",
                                                }}
                                              >
                                                Pkg {packageData.BoxNo}
                                              </span>
                                              : {packageData.BookTrackDate}{" "}
                                              {packageData.BookTrackTime}
                                              <br />
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              textTransform={"lowercase"}
                                              sx={{
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                                overflowWrap: "anywhere",
                                              }}
                                               >
                                              {packageData.Remarks || " "}
                                            </Typography>
                                          </div>
                                        ))}
                                    </Card>
                                  </TimelineContent>
                                </TimelineItem>
                              ))
                          )}
                        </Timeline>
                      </Stack>
                    </CardContent>
                  </Card>
                </Box>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </div>
  );
};

export default ShipmentTracking;